// pages/UserManagement/AllUsers.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Stack,
  Switch,
  debounce,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Category as SpeciesIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  CameraAlt as CameraIcon,
} from "@mui/icons-material";
import { getCurrentUser, getAuthToken } from "../../utils/AuthHelper";

const API_BASE_URL = "https://api.tpilums.org.pk";
// const API_BASE_URL = "http://127.0.0.1:8000";

export default function AllUsers() {
  const user = getCurrentUser();
  const token = getAuthToken();
  const isMounted = useRef(true);

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [totalUsers, setTotalUsers] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    user_type: "",
    organization: "",
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);

  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userForPermissions, setUserForPermissions] = useState(null);
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const [cameraSpeciesDialogOpen, setCameraSpeciesDialogOpen] = useState(false);
  const [userForCameraSpecies, setUserForCameraSpecies] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraAssignments, setSelectedCameraAssignments] = useState(
    {},
  );
  const [cameraSpeciesLoading, setCameraSpeciesLoading] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const debouncedSearch = useRef(
    debounce((value) => {
      setSearchTerm(value);
      setPage(0);
    }, 500),
  ).current;

  const fetchUsers = async (url = null) => {
    try {
      const headers = { Authorization: `Token ${token}` };
      let usersRes;

      const buildQueryParams = (baseUrl) => {
        const url = new URL(baseUrl);
        const params = new URLSearchParams(url.search);
        if (searchTerm) params.append("search", searchTerm);
        if (userTypeFilter && userTypeFilter !== "all")
          params.append("user_type", userTypeFilter);
        if (user?.user_type === "admin" && orgFilter && orgFilter !== "all")
          params.append("organization", orgFilter);
        params.append("page", page + 1);
        params.append("page_size", rowsPerPage);
        return `${url.origin}${url.pathname}?${params.toString()}`;
      };

      if (url) {
        const response = await fetch(url, { headers });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        usersRes = await response.json();
        return usersRes;
      } else if (user?.user_type === "admin") {
        const response = await fetch(
          buildQueryParams(`${API_BASE_URL}/accounts/api/users/`),
          { headers },
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        usersRes = await response.json();
      } else if (user?.user_type === "superuser") {
        const response = await fetch(
          buildQueryParams(
            `${API_BASE_URL}/accounts/api/users/my_organization_users/`,
          ),
          { headers },
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        usersRes = await response.json();
      } else {
        return { results: [], count: 0 };
      }

      return {
        results: Array.isArray(usersRes.results) ? usersRes.results : [],
        count: usersRes.count || 0,
      };
    } catch (err) {
      console.error("Error fetching users:", err);
      throw err;
    }
  };

  const fetchData = async (pageUrl = null, isInitial = false) => {
    if (!isMounted.current) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };
      const usersData = await fetchUsers(pageUrl);
      if (isMounted.current) {
        setUsers(usersData.results || []);
        setTotalUsers(usersData.count || 0);
      }

      if (isInitial || !organizations.length) {
        if (user?.user_type === "admin") {
          const orgsRes = await fetch(
            `${API_BASE_URL}/accounts/api/organizations/`,
            { headers },
          );
          if (orgsRes.ok) {
            const orgsData = await orgsRes.json();
            if (isMounted.current)
              setOrganizations(orgsData.results || orgsData || []);
          }
        }
      }

      if (user?.user_type === "admin" && isInitial) {
        const speciesRes = await fetch(`${API_BASE_URL}/core/api/specie/`, {
          headers,
        });
        if (speciesRes.ok) {
          const speciesData = await speciesRes.json();
          if (isMounted.current) {
            setAvailableSpecies(
              (speciesData.results || speciesData || []).map((s) => ({
                ...s,
                id: s.id,
              })),
            );
          }
        }
      }

      if (
        user?.user_type === "superuser" &&
        user?.organization?.id &&
        isInitial
      ) {
        const orgSpeciesRes = await fetch(
          `${API_BASE_URL}/core/api/organization-species-permissions/?organization=${user.organization.id}`,
          { headers },
        );
        if (orgSpeciesRes.ok) {
          const permData = await orgSpeciesRes.json();
          const permissions = permData.results || permData || [];
          const speciesPromises = permissions.map(async (perm) => {
            const specieId =
              typeof perm.specie === "object" ? perm.specie.id : perm.specie;
            try {
              const encodedSpecieId = encodeURIComponent(specieId);
              const specieRes = await fetch(
                `${API_BASE_URL}/core/api/specie/${encodedSpecieId}/`,
                { headers },
              );
              if (specieRes.ok) return await specieRes.json();
            } catch (err) {
              console.error(`Error fetching species ${specieId}:`, err);
            }
            return null;
          });
          const speciesList = (await Promise.all(speciesPromises)).filter(
            (s) => s !== null,
          );
          if (isMounted.current) setAvailableSpecies(speciesList);
        }

        // Fetch ALL cameras in organization (paginate through all pages)
        let allCameras = [];
        let nextUrl = `${API_BASE_URL}/core/api/camera/?page_size=100`;
        while (nextUrl) {
          const camerasRes = await fetch(nextUrl, { headers });
          if (camerasRes.ok) {
            const camerasData = await camerasRes.json();
            allCameras = [...allCameras, ...(camerasData.results || [])];
            nextUrl = camerasData.next;
          } else {
            break;
          }
        }
        const orgCameras = allCameras.filter(
          (c) =>
            c.organization === user.organization.id ||
            c.organization?.id === user.organization.id,
        );
        if (isMounted.current) setAvailableCameras(orgCameras);
      }

      if (isMounted.current) {
        setLoading(false);
        if (isInitial) setIsInitialLoad(false);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      if (isMounted.current) {
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);
    debouncedSearch(value);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === "userType") setUserTypeFilter(value);
    else if (filterType === "org") setOrgFilter(value);
    setPage(0);
    fetchData(null, false);
  };

  useEffect(() => {
    if (isInitialLoad) return;
    fetchData(null, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, userTypeFilter, orgFilter]);

  useEffect(() => {
    fetchData(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserTypeIcon = (type) => {
    switch (type) {
      case "admin":
        return <AdminIcon />;
      case "superuser":
        return <SupervisorIcon />;
      case "user":
        return <PersonIcon />;
      case "viewer":
        return <VisibilityIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case "admin":
        return "error";
      case "superuser":
        return "warning";
      case "user":
        return "success";
      case "viewer":
        return "info";
      default:
        return "default";
    }
  };

  const handleOpenEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setEditForm({
      username: userItem.username || "",
      email: userItem.email || "",
      first_name: userItem.first_name || "",
      last_name: userItem.last_name || "",
      user_type: userItem.user_type || "",
      organization: userItem.organization?.id || "",
      is_active: userItem.is_active ?? true,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditForm({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      user_type: "",
      organization: "",
      is_active: true,
    });
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };
      const payload = {
        username: editForm.username,
        email: editForm.email,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        user_type: editForm.user_type,
        is_active: editForm.is_active,
      };
      if (user?.user_type === "admin" && editForm.organization)
        payload.organization = editForm.organization;

      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${selectedUser.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) throw new Error("Failed to update user");

      setSuccess("User updated successfully");
      await fetchData(null, false);
      handleCloseEditDialog();
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleUserStatus = async (userItem) => {
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };
      const payload = { is_active: !userItem.is_active };
      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${userItem.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) throw new Error("Failed to update user status");
      setSuccess(
        `User ${userItem.is_active ? "deactivated" : "activated"} successfully`,
      );
      await fetchData(null, false);
    } catch (err) {
      console.error("Error toggling user status:", err);
      setError("Failed to update user status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${userId}/`,
        { method: "DELETE", headers },
      );
      if (!response.ok) throw new Error("Failed to delete user");
      setSuccess("User deleted successfully");
      await fetchData(null, false);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user");
    }
  };

  const handleOpenUserPermissionsDialog = async (userItem) => {
    setUserForPermissions(userItem);
    setPermissionsLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };
      const response = await fetch(
        `${API_BASE_URL}/core/api/user-species-permissions/?user=${userItem.id}`,
        { headers },
      );
      if (!response.ok) throw new Error("Failed to fetch permissions");
      const data = await response.json();
      const permissions = data.results || data || [];
      const assignedSpeciesIds = permissions.map((p) =>
        typeof p.specie === "object" ? p.specie.id : p.specie,
      );
      setSelectedSpecies(assignedSpeciesIds);
      setPermissionsDialogOpen(true);
    } catch (err) {
      console.error("Error fetching user species permissions:", err);
      setError("Failed to load species permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleSaveUserPermissions = async () => {
    if (!userForPermissions) return;
    setPermissionsLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };
      const currentRes = await fetch(
        `${API_BASE_URL}/core/api/user-species-permissions/?user=${userForPermissions.id}`,
        { headers },
      );
      if (!currentRes.ok)
        throw new Error("Failed to fetch current permissions");
      const currentData = await currentRes.json();
      const currentPermissions = currentData.results || currentData || [];
      const validCurrentPermissions = currentPermissions.filter(
        (perm) =>
          perm.user === userForPermissions.id ||
          (typeof perm.user === "object" &&
            perm.user.id === userForPermissions.id),
      );

      const permissionsToDelete = validCurrentPermissions.filter((perm) => {
        const specieId =
          typeof perm.specie === "object" ? perm.specie.id : perm.specie;
        return !selectedSpecies.includes(specieId);
      });
      for (const perm of permissionsToDelete) {
        const deleteRes = await fetch(
          `${API_BASE_URL}/core/api/user-species-permissions/${perm.id}/`,
          { method: "DELETE", headers },
        );
        if (!deleteRes.ok)
          throw new Error(`Failed to delete permission ${perm.id}`);
      }

      const existingSpecies = validCurrentPermissions.map((p) =>
        typeof p.specie === "object" ? p.specie.id : p.specie,
      );
      const speciesToAdd = selectedSpecies.filter(
        (specieId) => !existingSpecies.includes(specieId),
      );
      for (const specieId of speciesToAdd) {
        const createRes = await fetch(
          `${API_BASE_URL}/core/api/user-species-permissions/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              user: userForPermissions.id,
              specie: specieId,
            }),
          },
        );
        if (!createRes.ok) {
          const errorData = await createRes.json().catch(() => ({}));
          if (createRes.status === 403)
            throw new Error(
              "You don't have permission to assign this species.",
            );
          else
            throw new Error(
              errorData.detail ||
                `Failed to create permission for species ID ${specieId}`,
            );
        }
      }
      setSuccess(
        `User species permissions updated successfully for ${userForPermissions.username}`,
      );
      setPermissionsDialogOpen(false);
      setUserForPermissions(null);
      setSelectedSpecies([]);
      await fetchData(null, false);
    } catch (err) {
      console.error("Error saving user species permissions:", err);
      setError(err.message || "Failed to update species permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleOpenCameraSpeciesDialog = async (userItem) => {
    setUserForCameraSpecies(userItem);
    setCameraSpeciesLoading(true);
    setSelectedCameraAssignments({});
    try {
      const headers = { Authorization: `Token ${token}` };

      // Fetch existing camera-species permissions for this user
      const response = await fetch(
        `${API_BASE_URL}/core/api/camera-species-permissions/?user=${userItem.id}`,
        { headers },
      );
      if (!response.ok) throw new Error("Failed to fetch camera permissions");

      const data = await response.json();
      const permissions = data.results || data || [];

      // Build initial state: { camera_id: [species_ids] }
      const initialAssignments = {};
      permissions.forEach((perm) => {
        const cameraId =
          typeof perm.camera === "object" ? perm.camera.id : perm.camera;
        const speciesId =
          typeof perm.species === "object" ? perm.species.id : perm.species;
        if (!initialAssignments[cameraId]) {
          initialAssignments[cameraId] = [];
        }
        initialAssignments[cameraId].push(speciesId);
      });

      console.log("Existing permissions:", initialAssignments);
      setSelectedCameraAssignments(initialAssignments);
      setCameraSpeciesDialogOpen(true);
    } catch (err) {
      console.error("Error fetching camera species permissions:", err);
      setError("Failed to load camera permissions");
    } finally {
      setCameraSpeciesLoading(false);
    }
  };

  const handleCameraSpeciesToggle = (cameraId, specieId) => {
    setSelectedCameraAssignments((prev) => {
      const newAssignments = { ...prev };
      if (!newAssignments[cameraId]) newAssignments[cameraId] = [];
      if (newAssignments[cameraId].includes(specieId)) {
        newAssignments[cameraId] = newAssignments[cameraId].filter(
          (id) => id !== specieId,
        );
        if (newAssignments[cameraId].length === 0)
          delete newAssignments[cameraId];
      } else {
        newAssignments[cameraId].push(specieId);
      }
      return newAssignments;
    });
  };

  const handleSaveCameraSpecies = async () => {
    if (!userForCameraSpecies) return;
    setCameraSpeciesLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      // Debug logs
      console.log("Saving for user:", userForCameraSpecies.id);
      console.log("Selected assignments:", selectedCameraAssignments);

      // Get existing permissions for this user
      const existingRes = await fetch(
        `${API_BASE_URL}/core/api/camera-species-permissions/?user=${userForCameraSpecies.id}`,
        { headers },
      );
      if (!existingRes.ok)
        throw new Error("Failed to fetch existing permissions");
      const existingData = await existingRes.json();
      const existingPermissions = existingData.results || existingData || [];

      // Build map of existing permissions: {camera_id: {species_id: permission_id}}
      const existingMap = {};
      for (const perm of existingPermissions) {
        const cameraId =
          typeof perm.camera === "object" ? perm.camera.id : perm.camera;
        const speciesId =
          typeof perm.species === "object" ? perm.species.id : perm.species;
        if (!existingMap[cameraId]) existingMap[cameraId] = {};
        existingMap[cameraId][speciesId] = perm.id;
      }

      // Build new assignments map: {camera_id: [species_ids]}
      const newAssignments = {};
      for (const [cameraId, speciesIds] of Object.entries(
        selectedCameraAssignments,
      )) {
        const validSpeciesIds = speciesIds.filter(
          (id) => id !== undefined && id !== null && id !== "",
        );
        if (validSpeciesIds.length > 0) {
          newAssignments[parseInt(cameraId)] = validSpeciesIds;
        }
      }

      // 1. Delete permissions that are no longer selected
      for (const [cameraId, speciesMap] of Object.entries(existingMap)) {
        const cameraIdInt = parseInt(cameraId);
        const newSpeciesForCamera = newAssignments[cameraIdInt] || [];

        for (const [speciesId, permissionId] of Object.entries(speciesMap)) {
          if (!newSpeciesForCamera.includes(speciesId)) {
            // This permission is no longer selected - delete it
            const deleteRes = await fetch(
              `${API_BASE_URL}/core/api/camera-species-permissions/${permissionId}/`,
              { method: "DELETE", headers },
            );
            if (!deleteRes.ok)
              throw new Error("Failed to remove old permission");
            console.log(
              `Deleted permission: camera ${cameraId}, species ${speciesId}`,
            );
          }
        }
      }

      // 2. Add new permissions that don't exist yet
      for (const [cameraId, speciesIds] of Object.entries(newAssignments)) {
        const cameraIdInt = parseInt(cameraId);
        const existingSpeciesForCamera = existingMap[cameraIdInt] || {};

        for (const speciesId of speciesIds) {
          if (!existingSpeciesForCamera[speciesId]) {
            // This permission doesn't exist yet - create it
            const createRes = await fetch(
              `${API_BASE_URL}/core/api/camera-species-permissions/`,
              {
                method: "POST",
                headers,
                body: JSON.stringify({
                  user: userForCameraSpecies.id,
                  camera: cameraIdInt,
                  species: speciesId,
                }),
              },
            );
            if (!createRes.ok) {
              const errorData = await createRes.json().catch(() => ({}));
              console.error("Create permission error:", errorData);
              throw new Error(
                errorData.detail ||
                  errorData.species?.[0] ||
                  errorData.camera?.[0] ||
                  errorData.user?.[0] ||
                  `Failed to assign camera ${cameraId} species ${speciesId}`,
              );
            }
            console.log(
              `Created permission: camera ${cameraId}, species ${speciesId}`,
            );
          }
        }
      }

      setSuccess(
        `Camera species permissions updated for ${userForCameraSpecies.username}`,
      );
      setCameraSpeciesDialogOpen(false);
      setUserForCameraSpecies(null);
      setSelectedCameraAssignments({});
      await fetchData(null, false);
    } catch (err) {
      console.error("Error saving camera species permissions:", err);
      setError(err.message || "Failed to save camera permissions");
    } finally {
      setCameraSpeciesLoading(false);
    }
  };

  const getOrganizationName = (org) => {
    if (!org) return "No organization";
    return typeof org === "object" ? org.name : "Organization";
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userTypeFilter}
                label="User Type"
                onChange={(e) => handleFilterChange("userType", e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="admin">System Admin</MenuItem>
                <MenuItem value="superuser">Organization Admin</MenuItem>
                <MenuItem value="user">Regular User</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {user?.user_type === "admin" && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={orgFilter}
                  label="Organization"
                  onChange={(e) => handleFilterChange("org", e.target.value)}
                >
                  <MenuItem value="all">All Organizations</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchData(null, true)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((userItem) => (
                <TableRow
                  key={userItem.id}
                  hover
                  sx={{
                    opacity: userItem.is_active ? 1 : 0.6,
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        {userItem.first_name?.[0] ||
                          userItem.username?.[0] ||
                          "U"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {userItem.first_name} {userItem.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{userItem.username}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{userItem.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getUserTypeIcon(userItem.user_type)}
                      label={userItem.user_type_display || userItem.user_type}
                      color={getUserTypeColor(userItem.user_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {userItem.organization ? (
                      <Chip
                        icon={<BusinessIcon />}
                        label={getOrganizationName(userItem.organization)}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No organization
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        userItem.is_active ? (
                          <CheckCircleIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                      label={userItem.is_active ? "Active" : "Inactive"}
                      color={userItem.is_active ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(userItem.date_created).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(userItem)}
                            disabled={userItem.id === user?.id}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={userItem.is_active ? "Deactivate" : "Activate"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleUserStatus(userItem)}
                            disabled={userItem.id === user?.id}
                          >
                            {userItem.is_active ? (
                              <LockOpenIcon fontSize="small" />
                            ) : (
                              <LockIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {(user?.user_type === "admin" ||
                        (user?.user_type === "superuser" &&
                          userItem.user_type === "user" &&
                          userItem.organization?.id ===
                            user?.organization?.id)) && (
                        <Tooltip title="Assign Species to User">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenUserPermissionsDialog(userItem)
                              }
                            >
                              <SpeciesIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {(user?.user_type === "admin" ||
                        (user?.user_type === "superuser" &&
                          userItem.user_type === "user" &&
                          userItem.organization?.id ===
                            user?.organization?.id)) && (
                        <Tooltip title="Assign Cameras + Species">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleOpenCameraSpeciesDialog(userItem)
                              }
                            >
                              <CameraIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {user?.user_type === "admin" &&
                        userItem.id !== user?.id && (
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(userItem.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={editForm.username || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, username: e.target.value })
                }
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.first_name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, first_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.last_name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, last_name: e.target.value })
                }
              />
            </Grid>
            {user?.user_type === "admin" && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>User Type</InputLabel>
                    <Select
                      value={editForm.user_type || ""}
                      label="User Type"
                      onChange={(e) =>
                        setEditForm({ ...editForm, user_type: e.target.value })
                      }
                    >
                      <MenuItem value="admin">System Administrator</MenuItem>
                      <MenuItem value="superuser">
                        Organization Administrator
                      </MenuItem>
                      <MenuItem value="user">Regular User</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={editForm.organization || ""}
                      label="Organization"
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          organization: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="">No Organization</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.is_active ?? true}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.checked })
                    }
                  />
                }
                label="Active Account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Species Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => {
          setPermissionsDialogOpen(false);
          setUserForPermissions(null);
          setSelectedSpecies([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Species to User: {userForPermissions?.username}
        </DialogTitle>
        <DialogContent>
          {permissionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                {user?.user_type === "superuser"
                  ? "Select which species this user can access. You can only assign species that your organization has permission for."
                  : "Select which species this user can access and view in the system."}
              </Typography>
              {user?.user_type === "superuser" &&
                availableSpecies.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Your organization doesn't have any species assigned. Please
                    contact your system administrator.
                  </Alert>
                )}
              <Box sx={{ maxHeight: 400, overflow: "auto", mt: 2 }}>
                {availableSpecies.length > 0 ? (
                  availableSpecies.map((specie) => (
                    <Box
                      key={specie.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: selectedSpecies.includes(specie.id)
                          ? "action.selected"
                          : "background.paper",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: specie.color || "#1976d2",
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {specie.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {specie.id}
                          </Typography>
                        </Box>
                        {specie.endangered && (
                          <Chip label="Endangered" color="error" size="small" />
                        )}
                        {specie.enabled === false && (
                          <Chip label="Disabled" color="default" size="small" />
                        )}
                      </Box>
                      <Switch
                        checked={selectedSpecies.includes(specie.id) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSpecies([...selectedSpecies, specie.id]);
                          } else {
                            setSelectedSpecies(
                              selectedSpecies.filter((id) => id !== specie.id),
                            );
                          }
                        }}
                        disabled={specie.enabled === false}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    {user?.user_type === "superuser"
                      ? "No species have been assigned to your organization yet. Contact your system administrator."
                      : "No species available to assign. Contact your system administrator."}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermissionsDialogOpen(false);
              setUserForPermissions(null);
              setSelectedSpecies([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUserPermissions}
            variant="contained"
            disabled={permissionsLoading || availableSpecies.length === 0}
          >
            {permissionsLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Save Permissions"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Species Permissions Dialog */}
      <Dialog
        open={cameraSpeciesDialogOpen}
        onClose={() => {
          setCameraSpeciesDialogOpen(false);
          setUserForCameraSpecies(null);
          setSelectedCameraAssignments({});
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Assign Cameras & Species to: {userForCameraSpecies?.username}
        </DialogTitle>
        <DialogContent>
          {cameraSpeciesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                {user?.user_type === "superuser"
                  ? "Select which cameras this user can access, and for each camera, which species they can view."
                  : "Select which cameras and species this user can access."}
              </Typography>

              {availableCameras.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No cameras available in your organization. Please contact your
                  system administrator.
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                {availableCameras.map((camera) => (
                  <Paper
                    key={camera.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <CameraIcon color="primary" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {camera.description || `Camera ${camera.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {camera.id}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Select species visible from this camera:
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {availableSpecies.map((specie) => {
                        const isChecked =
                          selectedCameraAssignments[camera.id]?.includes(
                            specie.id,
                          ) || false;
                        return (
                          <Chip
                            key={`${camera.id}-${specie.id}`}
                            label={specie.name}
                            onClick={() =>
                              handleCameraSpeciesToggle(camera.id, specie.id)
                            }
                            sx={{
                              backgroundColor: isChecked
                                ? specie.color
                                : "#f0f0f0",
                              color: isChecked ? "white" : "#666",
                              "&:hover": { opacity: 0.8 },
                              cursor: "pointer",
                              border: `1px solid ${isChecked ? specie.color : "#e0e0e0"}`,
                            }}
                          />
                        );
                      })}
                      {availableSpecies.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No species available to assign.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCameraSpeciesDialogOpen(false);
              setUserForCameraSpecies(null);
              setSelectedCameraAssignments({});
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCameraSpecies}
            variant="contained"
            disabled={cameraSpeciesLoading || availableCameras.length === 0}
          >
            {cameraSpeciesLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Save Camera Permissions"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
